import '@material/web/icon/icon.js'
import '../viewparts/new-board-card.js'

import { css, html, LitElement, PropertyValues, nothing } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { keyed } from 'lit/directives/keyed.js'

@customElement('board-tile-list')
export class BoardTileList extends LitElement {
  static styles = [
    css`
      :host {
        overflow: auto;
        padding: var(--popup-content-padding);
        display: grid;
        background-color: var(--md-sys-color-background);

        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        grid-auto-rows: var(--card-list-rows-height);
        grid-gap: 20px;
      }

      [card] {
        position: relative;
        align-items: center;
        overflow: hidden;
      }

      [card][create] {
        overflow: visible;
        background-color: initial;
      }

      [card] > a {
        display: flex;
        clip-path: border-box;
      }

      [card]:hover {
        cursor: pointer;
      }

      [name] {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        margin-top: var(--spacing-small);
        width: calc(100% - 45px);
        color: var(--md-sys-color-on-background);
        font-weight: bolder;
        font-size: var(--fontsize-small);
      }

      img {
        flex: 1;
        object-fit: contain;
      }

      md-icon[iconBtn] {
        float: right;
        margin-top: -20px;
        margin-left: 2px;
        color: var(--board-list-star-color);
        font-size: 1.4em;
      }
      md-icon[info] {
        color: var(--md-sys-color-primary);
      }

      md-icon[iconBtn][favored],
      md-icon[info]:hover {
        color: var(--board-list-star-active-color);
      }

      a {
        display: block;
        border-radius: var(--card-list-border-radius);
        border: var(--border-dim-color);
        box-sizing: border-box;
        color: var(--card-list-color);
        background-color: var(--card-list-background-color);
        margin: 0px;
        height: calc(100% - 25px);
      }

      :host > *:hover [info] {
        opacity: 1;
        -webkit-transition: opacity 0.8s;
        -moz-transition: opacity 0.8s;
        -o-transition: opacity 0.8s;
        transition: opacity 0.8s;
      }

      [draggable='true'] {
        cursor: grab;
      }

      @media screen and (max-width: 800px), screen and (max-height: 600px) {
        new-board-card {
          display: none;
        }
      }
    `
  ]

  @property({ type: Array }) boards: any[] = []
  @property({ type: Array }) groups: any[] = []
  @property({ type: String }) group?: string
  @property({ type: Boolean }) creatable?: boolean = false
  @property({ type: String, attribute: 'search-text' }) searchText?: string
  @property({ type: Boolean, attribute: 'reorderable' }) reorderable: boolean = false

  private draggedItem

  connectedCallback() {
    super.connectedCallback()

    if (this.reorderable) {
      this.renderRoot.addEventListener('dragstart', (e: Event) => {
        const target = e.target! as HTMLElement
        this.draggedItem = target.closest('[card]')
        ;(e as DragEvent).dataTransfer?.setData('text/plain', target.innerHTML)
      })

      this.renderRoot.addEventListener('dragover', (e: Event) => {
        e.preventDefault()

        const target = e.target! as HTMLElement
        const targetItem = target.closest('[card]')
        if (targetItem && targetItem !== this.draggedItem) {
          const targetRect = targetItem.getBoundingClientRect()
          const mousePos = (e as DragEvent).clientX - targetRect.left

          if (mousePos < targetRect.width / 2) {
            // 마우스 위치가 아이템 좌측 반절에 있을 때
            this.renderRoot.insertBefore(this.draggedItem, targetItem)
          } else {
            // 마우스 위치가 아이템 우측 반절에 있을 때
            this.renderRoot.insertBefore(this.draggedItem, targetItem.nextSibling)
          }
        }
      })

      this.renderRoot.addEventListener('drop', (e: Event) => {
        e.preventDefault()

        const boardIds = Array.from(this.renderRoot.querySelectorAll('[card]'))
          .map(board => board.getAttribute('id'))
          .filter(Boolean)

        this.dispatchEvent(
          new CustomEvent('reordered', {
            detail: {
              groupId: this.group,
              boardIds
            }
          })
        )
      })
    }
  }

  render() {
    var boards = this.boards || []

    return html`
      ${this.creatable
        ? html` <new-board-card .groups=${this.groups} .defaultGroup=${this.group} @create-board=${e => this.onCreateBoard(e)} card create></new-board-card> `
        : nothing}
      ${keyed(
        Date.now(),
        boards.map(
          board => html`
            <div card draggable="true" id=${board.id}>
              <a href="board-viewer/${board.id}"> <img src=${board.thumbnail} /> </a>

              <div name>${board.name}</div>
              <!-- <div description>${board.description}</div> -->

              <md-icon
                iconBtn
                info
                @click=${e => {
                  this.infoBoard(board)
                  e.preventDefault()
                }}
                >info</md-icon
              >
            </div>
          `
        )
      )}
    `
  }

  updated(changes: PropertyValues<this>) {
    var creationCard = this.renderRoot.querySelector('new-board-card') as any
    if (creationCard) {
      creationCard.reset()
    }
  }

  onCreateBoard(e) {
    this.dispatchEvent(
      new CustomEvent('create-board', {
        detail: e.detail
      })
    )
  }

  infoBoard(board) {
    this.dispatchEvent(
      new CustomEvent('info-board', {
        detail: board
      })
    )
  }
}
